import { Label } from "./ui/label";
import { Timer, Sparkle, LockKey, Receipt } from "@phosphor-icons/react";

export const TRIAL_OPTIONS = [
  { value: 0, label: "Full access", hint: "no trial — invoiced / partner / complimentary" },
  { value: 7, label: "7-day trial" },
  { value: 14, label: "14-day trial" },
  { value: 21, label: "21-day trial" },
  { value: 30, label: "30-day trial" },
];

// Platform console → New Tenant: how the workspace starts (free trial length or straight to full access)
export function TrialAccessField({ value, onChange }) {
  return (
    <div data-testid="tenant-trial-field">
      <Label className="flex items-center gap-1"><Timer size={12} /> Access</Label>
      <div className="mt-1.5 grid grid-cols-5 gap-1.5" role="radiogroup" aria-label="Trial length">
        {TRIAL_OPTIONS.map((o) => {
          const on = Number(value) === o.value;
          return (
            <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.value)}
              className={`rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted/60"}`}
              data-testid={`tenant-trial-${o.value}`}>
              {o.label}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="tenant-trial-hint">
        {Number(value) === 0
          ? "Active straight away with every feature of the chosen plan — nothing to subscribe to."
          : `Trialing for ${value} days. When it runs out the workspace goes read-only (nothing is lost) with a link to the subscribe page until they subscribe.`}
      </p>
    </div>
  );
}

const day = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

// Tenant card: trial end / expired / complimentary access
export function TenantTrialBadge({ tenant: t }) {
  if (!t || t.is_master) return null;
  if (t.billing_mode === "invoiced") {
    return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`trial-badge-${t.slug}`}><Receipt size={9} weight="bold" /> Subscribed · invoiced</span>;
  }
  if (t.subscription_status === "trialing" && t.trial_ends_at) {
    const left = Math.ceil((new Date(t.trial_ends_at) - Date.now()) / 86400000);
    const ext = (t.trial_extensions || []).length;
    if (left > 0) {
      return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" data-testid={`trial-badge-${t.slug}`}><Timer size={9} weight="bold" /> Trial ends {day(t.trial_ends_at)} · {left}d{ext ? ` · +${ext} ext` : ""}</span>;
    }
    return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" data-testid={`trial-badge-${t.slug}`}><LockKey size={9} weight="bold" /> Trial expired{t.trial_enforced ? " · read-only" : ""}</span>;
  }
  if (t.billing_mode === "complimentary") {
    return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`trial-badge-${t.slug}`}><Sparkle size={9} weight="bold" /> Full access · no trial</span>;
  }
  return null;
}
